import { z } from 'zod';

/**
 * Event-Status Enum.
 * draft → published → (cancelled | completed)
 */
export const EventStatusSchema = z.enum(['draft', 'published', 'cancelled', 'completed']);

/** TypeScript Type für Event-Status */
export type EventStatus = z.infer<typeof EventStatusSchema>;

/**
 * Zod Schema für Event — Runtime-Validierung + Type-Inference.
 * Universelles Event-Model für alle Kategorien.
 * Kategorie-spezifische Felder sind nullable.
 *
 * Das `displayTitle`-Feld ist ein berechnetes Feld (nicht in DB gespeichert),
 * das in der API-Response aus Kategorie, Season und Titel zusammengesetzt wird.
 */
export const EventSchema = z.object({
  id: z.string().cuid(),
  categoryId: z.string().cuid(),
  title: z.string(),
  /** Berechneter Anzeigetitel: "{Kategorie} {Season} — {Titel}" oder nur "{Titel}" wenn keine Season */
  displayTitle: z.string().optional(),
  description: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  locationUrl: z.string().url().nullable().optional(),
  imageUrl: z.string().url().nullable().optional(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date().nullable().optional(),
  registrationStart: z.coerce.date().nullable().optional(),
  registrationEnd: z.coerce.date().nullable().optional(),
  maxParticipants: z.number().int().positive().nullable().optional(),
  isPublic: z.boolean().default(true),
  status: EventStatusSchema.default('draft'),
  // ICON League spezifisch
  opponent: z.string().nullable().optional(),
  opponentLogoUrl: z.string().url().nullable().optional(),
  ticketPrice: z.number().nonnegative().nullable().optional(),
  totalTickets: z.number().int().positive().nullable().optional(),
  // Google Forms Integration
  formId: z.string().nullable().optional(),
  formUrl: z.string().url().nullable().optional(),
  // Discord
  discordMessageId: z.string().nullable().optional(),
  discordInternalMessageId: z.string().nullable().optional(),
  discordChannelId: z.string().nullable().optional(),
  // Season
  seasonId: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type Event = z.infer<typeof EventSchema>;

/**
 * Schema für Event-Erstellung.
 */
export const CreateEventSchema = EventSchema.pick({
  categoryId: true,
  title: true,
  description: true,
  location: true,
  locationUrl: true,
  imageUrl: true,
  startsAt: true,
  endsAt: true,
  registrationStart: true,
  registrationEnd: true,
  maxParticipants: true,
  isPublic: true,
  status: true,
  opponent: true,
  opponentLogoUrl: true,
  ticketPrice: true,
  totalTickets: true,
  formId: true,
  formUrl: true,
  discordMessageId: true,
  discordInternalMessageId: true,
  discordChannelId: true,
  seasonId: true,
});

/** TypeScript Type für Event-Erstellung */
export type CreateEvent = z.infer<typeof CreateEventSchema>;

/**
 * Schema für Event-Update.
 * Alle Felder optional — PATCH-Semantik.
 */
export const UpdateEventSchema = CreateEventSchema.partial();

/** TypeScript Type für Event-Update */
export type UpdateEvent = z.infer<typeof UpdateEventSchema>;

/**
 * Berechnet den angezeigten Event-Titel aus Kategorie, Season und Titel.
 *
 * Wenn das Event einer Season zugeordnet ist und eine Kategorie hat, wird der
 * zusammengesetzte Titel verwendet: `{category.name} {season.name} — {event.title}`.
 * Andernfalls wird nur der kurze Titel zurückgegeben.
 *
 * @param event - Event-Objekt mit title, category und season
 * @returns Zusammengesetzter Anzeigetitel oder nur der kurze Titel
 *
 * @example
 * ```ts
 * computeDisplayTitle({ title: 'Spieltag 7', category: { name: 'Icon League' }, season: { name: 'Season 4' } })
 * // → 'Icon League Season 4 — Spieltag 7'
 *
 * computeDisplayTitle({ title: 'Spieltag 7', category: null, season: null })
 * // → 'Spieltag 7'
 * ```
 */
export function computeDisplayTitle(event: {
  title: string;
  category?: { name: string } | null;
  season?: { name: string } | null;
}): string {
  if (event.season && event.category) {
    return `${event.category.name} ${event.season.name} — ${event.title}`;
  }
  return event.title;
}

/**
 * Gibt die URL für den Veranstaltungsort zurück.
 * Wenn `locationUrl` gesetzt, wird diese direkt verwendet.
 * Andernfalls wird ein Google Maps Suchlink aus dem `location`-Text generiert.
 *
 * @param location - Name oder Adresse des Veranstaltungsorts
 * @param locationUrl - Optionale explizite URL (z.B. Google Maps Link oder Website)
 * @returns Vollständige URL die in einem Link verwendet werden kann
 *
 * @example
 * ```ts
 * getLocationUrl('Velodrom Berlin') // → Google Maps Link
 * getLocationUrl('Velodrom Berlin', 'https://velodrom.de') // → 'https://velodrom.de'
 * ```
 */
export function getLocationUrl(location: string, locationUrl?: string | null): string {
  if (locationUrl) return locationUrl;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
}
